import { Node } from "../types";
import { hasRecentEmploymentSupport } from '../employmentSupport';

export type BadgeType = 'neutral' | 'bad' | 'warning' | 'comment' | 'license' | 'leasedCars' | 'tenantCase' | 'employmentSupport';

export interface TenantCase {
  case_number: string;
  case_date: string;
  case_status: string;
}

export interface NodeBadge {
  type: BadgeType;
  value: string | number;
}

export interface BadgeLayoutItem extends NodeBadge {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const LEASED_CARS_BADGE_COLOR = '#7c3aed';
export const LEASED_CARS_ICON = '\uf1b9';
export const TENANT_CASE_BADGE_COLOR = '#991b1b';
export const EMPLOYMENT_SUPPORT_ICON = '\uf0b1';
export const EMPLOYMENT_SUPPORT_COLOR = '#0369a1';

const BADGE_SIZE = 24;
const BADGE_PADDING = 2;

export const getBadgeColor = (type: BadgeType): string => {
  switch (type) {
    case 'employmentSupport':
      return EMPLOYMENT_SUPPORT_COLOR;
    case 'neutral':
      return '#6c757d';
    case 'bad':
      return '#dc3545';
    case 'warning':
      return '#f39c12';
    case 'comment':
      return '#2563eb';
    case 'license':
      return '#0f766e';
    case 'leasedCars':
      return LEASED_CARS_BADGE_COLOR;
    case 'tenantCase':
      return TENANT_CASE_BADGE_COLOR;
  }
};

export const getTenantCases = (
  properties: Record<string, unknown> | null | undefined,
): TenantCase[] => {
  const tenantCases = properties?.tenant_cases;
  if (!Array.isArray(tenantCases)) return [];

  return tenantCases.filter((value): value is TenantCase => {
    if (!value || typeof value !== 'object') return false;

    const tenantCase = value as Record<string, unknown>;
    return typeof tenantCase.case_number === 'string'
      && tenantCase.case_number.trim() !== ''
      && typeof tenantCase.case_date === 'string'
      && tenantCase.case_date.trim() !== ''
      && typeof tenantCase.case_status === 'string'
      && tenantCase.case_status.trim() !== '';
  });
};

export const countOrganizationsWithTenantCases = (nodes: Node[]): number => {
  const organizationNumbers = new Set<string>();

  nodes.forEach((node) => {
    if (node.type !== 'Company' || getTenantCases(node.properties).length === 0) return;

    const organizationNumber = node.properties?.organization_number;
    organizationNumbers.add(
      typeof organizationNumber === 'string' && organizationNumber.trim() !== ''
        ? organizationNumber
        : node.id,
    );
  });

  return organizationNumbers.size;
};

const parseCarCount = (rawValue: unknown): number | null => {
  const parsedValue = typeof rawValue === 'number'
    ? rawValue
    : typeof rawValue === 'string' && rawValue.trim() !== ''
      ? Number(rawValue)
      : Number.NaN;

  return Number.isSafeInteger(parsedValue) && parsedValue >= 0 ? parsedValue : null;
};

export const hasCompanyVehicles = (properties: Record<string, unknown> | null | undefined): boolean => (
  [
    properties?.num_leased_cars,
    properties?.num_owned_cars,
    properties?.num_total_cars,
  ].some((value) => (parseCarCount(value) ?? 0) > 0)
);

// Keep missing counts distinct from zero; only derive a total when both parts are known.
export const getCarBadgeLabel = (properties: Record<string, unknown> | null | undefined): string | null => {
  const leased = parseCarCount(properties?.num_leased_cars);
  const owned = parseCarCount(properties?.num_owned_cars);
  const total = parseCarCount(properties?.num_total_cars)
    ?? (leased !== null && owned !== null ? parseCarCount(leased + owned) : null);

  if ((leased ?? 0) === 0 && (total ?? 0) === 0) return null;

  return `${leased ?? '—'} (${total ?? '—'})`;
};

export const getNodeBadges = (
  node: Node,
  options: {
    isExpanded: boolean;
    isSource: boolean;
    showCompanyComments: boolean;
    showIvoLicenses: boolean;
  }
): NodeBadge[] => {
  const properties = node.properties ?? {};
  const isCompany = node.type === 'Company';
  const showCounts = !options.isExpanded && !options.isSource;
  const companyCount = isCompany && showCounts ? (properties.next_layer_company_count || 0) : 0;
  const debtCount = isCompany && showCounts ? (properties.next_layer_active_debt_count || 0) : 0;
  const carBadgeLabel = isCompany ? getCarBadgeLabel(properties) : null;
  const tenantCaseCount = isCompany ? getTenantCases(properties).length : 0;
  const isOnWatchList = isCompany && node.isOnWatchList === true;
  const hasComment = options.showCompanyComments && properties.has_comment === true;
  const hasIvoLicense = isCompany && options.showIvoLicenses && properties.has_ivo_license === true;
  const badges: NodeBadge[] = [];

  if (companyCount > 0) badges.push({ type: 'neutral', value: companyCount });
  if (debtCount > 0) badges.push({ type: 'bad', value: debtCount });
  if (isOnWatchList) badges.push({ type: 'warning', value: '!' });
  if (hasIvoLicense) badges.push({ type: 'license', value: 'L' });
  if (isCompany && hasRecentEmploymentSupport(properties)) badges.push({ type: 'employmentSupport', value: EMPLOYMENT_SUPPORT_ICON });
  if (hasComment) badges.push({ type: 'comment', value: 'C' });
  if (carBadgeLabel !== null) badges.push({ type: 'leasedCars', value: carBadgeLabel });
  if (tenantCaseCount > 0) badges.push({ type: 'tenantCase', value: tenantCaseCount });

  return badges;
};

export const getBadgeDimensions = (badge: NodeBadge): { width: number; height: number } => ({
  width: badge.type === 'leasedCars'
    ? Math.max(40, 24 + badge.value.toString().length * 6)
    : badge.type === 'tenantCase'
      ? Math.max(38, 28 + badge.value.toString().length * 6)
      : BADGE_SIZE,
  height: BADGE_SIZE,
});

export const layoutBadges = (badges: NodeBadge[]): BadgeLayoutItem[] => {
  const rowOffsets = [0, 0];

  return badges.map((badge, index) => {
    const row = index % 2;
    const { width, height } = getBadgeDimensions(badge);
    const x = rowOffsets[row];
    rowOffsets[row] += width + BADGE_PADDING;

    return {
      ...badge,
      x,
      y: row * (BADGE_SIZE + BADGE_PADDING),
      width,
      height,
    };
  });
};
